import React, { useEffect, useRef, useState } from "react";

// Legacy keys kept so the existing section components keep compiling.
// Visual look now comes from the stylesheet below (wb-legal__*).
const styles = {
  container: { display: "contents" },
  sectionDivider: { display: "none" },
  sectionLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "5px 11px",
    borderRadius: "999px",
    background: "rgba(58,110,212,0.08)",
    border: "1px solid rgba(58,110,212,0.22)",
    color: "#3A6ED4",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    width: "fit-content",
    margin: "56px 0 12px",
  },
  sectionTitle: {
    fontSize: "clamp(22px, 2.6vw, 30px)",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    margin: "0 0 8px",
    color: "#0B1020",
    paddingBottom: "16px",
    borderBottom: "1px solid #E6EAF2",
    scrollMarginTop: "96px",
  },
  heading: {
    fontSize: "clamp(17px, 1.6vw, 19px)",
    fontWeight: 700,
    letterSpacing: "-0.005em",
    color: "#0B1020",
    marginTop: "36px",
    marginBottom: "12px",
    scrollMarginTop: "96px",
  },
  paragraph: {
    margin: "0 0 14px",
    color: "#2B3346",
    lineHeight: 1.75,
    fontSize: "15.5px",
  },
  backBtn: { display: "none" },
};

const LEGAL_CSS = `
.wb-legal {
  --wb-bg: #FFFFFF;
  --wb-ink: #0B1020;
  --wb-mute: #5A6480;
  --wb-line: #E6EAF2;
  --wb-soft: #F7F9FC;
  --wb-brand: #3A6ED4;
  --wb-brand-2: #5BC3BE;
  background: var(--wb-bg);
  color: var(--wb-ink);
  font-family: 'Poppins', sans-serif;
  padding-bottom: 96px;
}
.wb-legal * { box-sizing: border-box; }

/* Hero */
.wb-legal__hero {
  position: relative;
  overflow: hidden;
  padding: 96px 40px 72px;
  background: linear-gradient(180deg, #F2F6FF 0%, #FFFFFF 100%);
  border-bottom: 1px solid var(--wb-line);
  isolation: isolate;
}
.wb-legal__hero-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
  z-index: -1;
}
.wb-legal__hero-orb--a {
  width: 420px; height: 420px;
  top: -140px; left: -120px;
  background: radial-gradient(circle, rgba(58,110,212,0.22), transparent 70%);
}
.wb-legal__hero-orb--b {
  width: 360px; height: 360px;
  bottom: -120px; right: -80px;
  background: radial-gradient(circle, rgba(91,195,190,0.18), transparent 70%);
}
.wb-legal__hero-inner {
  max-width: 1040px; margin: 0 auto;
  display: flex; flex-direction: column; gap: 18px;
}
.wb-legal__eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(58,110,212,0.08);
  border: 1px solid rgba(58,110,212,0.22);
  color: var(--wb-brand);
  font-size: 12px; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  width: fit-content;
}
.wb-legal__eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--wb-brand-2);
  box-shadow: 0 0 0 4px rgba(91,195,190,0.18);
}
.wb-legal__title {
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.05;
  font-size: clamp(40px, 6vw, 64px);
  color: var(--wb-ink);
}
.wb-legal__lede {
  max-width: 720px;
  color: var(--wb-mute);
  line-height: 1.65;
  font-size: clamp(15px, 1.5vw, 18px);
  margin: 0;
}
.wb-legal__meta {
  display: flex; flex-wrap: wrap; gap: 10px;
  margin-top: 8px;
}
.wb-legal__meta-pill {
  padding: 7px 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid var(--wb-line);
  font-size: 12.5px;
  color: var(--wb-mute);
}
.wb-legal__meta-pill strong { color: var(--wb-ink); font-weight: 600; }

/* Body grid */
.wb-legal__wrap {
  max-width: 1240px;
  margin: 0 auto;
  padding: 64px 40px 0;
}
.wb-legal__grid {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 56px;
  align-items: start;
}

/* TOC */
.wb-legal__toc {
  position: sticky;
  top: 96px;
  align-self: start;
  max-height: calc(100vh - 112px);
  overflow-y: auto;
}
.wb-legal__toc-inner { padding-right: 8px; }
.wb-legal__toc-title {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--wb-mute);
  margin-bottom: 14px;
  font-size: 11px;
  font-weight: 700;
}
.wb-legal__toc-group { margin-bottom: 20px; }
.wb-legal__toc-group-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--wb-ink);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 8px;
  padding: 0 14px;
  text-decoration: none;
  cursor: pointer;
}
.wb-legal__toc-group-label:hover { color: var(--wb-brand); }
.wb-legal__toc-list {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 2px;
  border-left: 1px solid var(--wb-line);
}
.wb-legal__toc-link {
  display: flex; align-items: center; gap: 10px;
  padding: 7px 14px;
  margin-left: -1px;
  border-left: 2px solid transparent;
  font-size: 13px;
  color: var(--wb-mute);
  text-decoration: none;
  transition: color 0.15s, border-color 0.2s, background 0.15s;
}
.wb-legal__toc-link:hover { color: var(--wb-ink); background: var(--wb-soft); }
.wb-legal__toc-link--active {
  color: var(--wb-brand);
  border-left-color: var(--wb-brand);
  font-weight: 600;
}
.wb-legal__toc-marker {
  width: 4px; height: 4px; border-radius: 50%;
  background: currentColor; opacity: 0.6;
  flex-shrink: 0;
}

/* Article */
.wb-legal__article { max-width: 760px; min-width: 0; }
.wb-legal__article h2 { scroll-margin-top: 96px; }
.wb-legal__article h5 { scroll-margin-top: 96px; }
.wb-legal__article a {
  color: var(--wb-brand);
  text-decoration: none;
  border-bottom: 1px solid rgba(58,110,212,0.3);
  transition: border-color 0.15s;
}
.wb-legal__article a:hover { border-bottom-color: var(--wb-brand); }
.wb-legal__article strong { color: var(--wb-ink); font-weight: 600; }
.wb-legal__article ul {
  margin: 14px 0 18px 0 !important;
  padding-left: 28px !important;
  color: var(--wb-ink) !important;
  line-height: 1.75 !important;
  font-size: 15.5px !important;
  list-style-type: disc !important;
  list-style-position: outside !important;
  display: block !important;
}
.wb-legal__article ul li {
  margin-bottom: 8px !important;
  display: list-item !important;
  padding-left: 4px !important;
  list-style-type: disc !important;
}
.wb-legal__article ul li:last-child {
  margin-bottom: 0 !important;
}

/* Contact card */
.wb-legal__contact {
  margin-top: 64px;
  padding: 28px 32px;
  border-radius: 18px;
  background: linear-gradient(180deg, #F2F6FF, #FFFFFF);
  border: 1px solid var(--wb-line);
  font-size: 15px;
  color: var(--wb-ink);
  line-height: 1.6;
}
.wb-legal__contact a {
  color: var(--wb-brand);
  text-decoration: none;
  font-weight: 600;
  border-bottom: 1px solid rgba(58,110,212,0.3);
}
.wb-legal__contact a:hover { border-bottom-color: var(--wb-brand); }

/* Back to top */
.wb-legal__top {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 200;
  width: 46px; height: 46px;
  border-radius: 999px;
  background: #0B1020;
  color: #fff;
  border: none;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(11,16,32,0.25);
  opacity: 0; transform: translateY(8px);
  pointer-events: none;
  transition: opacity 0.25s, transform 0.25s, background 0.2s;
  font-size: 18px;
  font-family: inherit;
}
.wb-legal__top:hover { background: #1A2035; }
.wb-legal__top[data-show] {
  opacity: 1; transform: translateY(0);
  pointer-events: auto;
}

/* Responsive */
@media (max-width: 960px) {
  .wb-legal__hero { padding: 72px 24px 56px; }
  .wb-legal__wrap { padding: 40px 24px 0; }
  .wb-legal__grid { grid-template-columns: 1fr; gap: 32px; }
  .wb-legal__toc {
    position: static;
    order: -1;
    padding: 14px 16px;
    border-radius: 14px;
    background: var(--wb-soft);
    border: 1px solid var(--wb-line);
    max-height: none;
  }
  .wb-legal__toc-list { border-left: none; }
  .wb-legal__toc-link { border-left: none; padding: 6px 0; }
  .wb-legal__toc-link--active { background: transparent; }
  .wb-legal__toc-group-label { padding: 0; }
}
@media (max-width: 520px) {
  .wb-legal__hero { padding: 56px 20px 44px; }
  .wb-legal__wrap { padding: 32px 20px 0; }
  .wb-legal__contact { padding: 22px 20px; }
}
`;

const fmtDate = (v) => {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return typeof v === "string" ? v : "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const slugify = (s, fallback) =>
  ((s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || fallback);


const TermsOfUseSection = () => (
  <div>
    <p style={styles.sectionLabel}>Terms of Use</p>
    <h2 style={styles.sectionTitle}>Terms of Use</h2>
    <h5 style={styles.heading}>1. Introduction</h5>
    <>{
  <p>
    These Terms of Use (&quot;Terms&quot;) are a legally binding agreement between you and workbrook LLC or, where applicable, the workbrook affiliate identified in these Terms (&quot;workbrook,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), and govern your access to and use of the workbrook website, mobile applications, web application, software, recruiter portal, referral platform, communications, and related services (collectively, the &quot;Services&quot;).
By accessing or using the Services, creating an account, posting a job, making a referral, applying for a job, engaging a recruiter, purchasing a paid service, or otherwise interacting with the platform, you agree to be bound by these Terms and the workbrook Privacy Policy. If you do not agree, you must not use the Services.
If you use the Services on behalf of a company, employer, recruitment agency, or other legal entity, you represent and warrant that you have authority to bind that entity to these Terms. In that case, &quot;you&quot; and &quot;your&quot; refer to both you and that entity.
You must be at least 16 years old to use the Services, or older if the laws of your jurisdiction require a higher minimum age. The English version of these Terms controls to the extent any translated version conflicts with it.
  </p>
}</>
    <h5 style={styles.heading}>2. Definitions</h5>
    <>{
  <p>
    For purposes of these Terms: &quot;Applicant&quot; means a user who applies for a job through the Services. &quot;Candidate&quot; means an individual who is identified, referred, sourced, applies, interviews, or is considered for a role through the Services. &quot;Employer&quot; or &quot;Job Poster&quot; means a user or organization that posts a job, advertises an opportunity, or uses the Services to hire talent. &quot;Recruiter&quot; means a user approved by workbrook to offer recruiting or talent acquisition services through the recruiter portal. &quot;Referrer&quot; means a user who shares a job opportunity through workbrook and refers a potential Applicant.
&quot;Referral&quot; means a tracked submission, share, recommendation, or platform-recognized introduction of a Candidate to a job through workbrook. &quot;Hire&quot; means an Applicant or Candidate beginning paid employment, consulting, contract work, or other compensated engagement with an Employer or its affiliate. &quot;Start Date&quot; means the first day on which the hired Candidate begins paid work in the applicable role. &quot;Wallet&quot; means an internal account within the Services that reflects a user&apos;s prepaid purchasing capacity for workbrook services. Funds added to a Wallet are converted into platform credits or entitlements usable solely within the Services and do not constitute a bank account, stored-value account, or deposit. Wallet funding, credits, and prepaid balances are governed by the applicable Candidate Product Terms and Employer Terms, including the &quot;Prepaid Services; No Stored Funds&quot; provisions
  </p>
}</>
    <h5 style={styles.heading}>3. Eligibility, Registration, and Accounts</h5>
    <>{
  <p>
    To use certain features of the Services, you must create an account and provide accurate, current, and complete information. You agree to keep your information updated at all times and to maintain a valid email address that you own and control.
You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You must notify workbrook promptly if you believe your account or security credentials have been compromised or used without authorization.
workbrook may permit sign-in through third-party services or social networking platforms. If you use such a login method, you authorize workbrook to receive information from that third party in accordance with your settings and permissions on that service.
workbrook may reject, suspend, restrict, or terminate any account where we reasonably believe the account information is false, incomplete, unauthorized, duplicative, fraudulent, abusive, or otherwise inconsistent with these Terms.
  </p>
}</>
    <h5 style={styles.heading}>4. Use on Behalf of an Organization</h5>
    <>{
  <p>
    If you use the Services on behalf of an employer, company, recruitment agency, or other legal entity, you represent and warrant that you are duly authorized to act on behalf of that entity, that the entity agrees to be bound by these Terms, and that you will use the Services only within the scope of your authority.
Organizations are responsible for activity conducted through accounts they authorize or administer. If you falsely claim authority to act on behalf of an entity, workbrook may suspend or terminate your account and pursue any remedies available under law or contract.
  </p>
}</>
    <h5 style={styles.heading}>5. Intellectual Property and Limited License</h5>
    <>{
  <p>
    The Services, including all software, design, layout, text, graphics, logos, trademarks, interface elements, platform features, databases, and other content made available by workbrook, are owned by or licensed to workbrook and are protected by intellectual property and other applicable laws.
Subject to your compliance with these Terms, workbrook grants you a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to access and use the Services for their intended purposes.
Except as expressly permitted by workbrook in writing, you may not copy, reproduce, republish, distribute, sell, license, rent, lease, transfer, or commercially exploit the Services or workbrook content; modify, adapt, translate, create derivative works from, decompile, reverse engineer, or disassemble the Services; scrape, extract, harvest, index, or use data from the Services for dataset creation, model training, or any unauthorized commercial purpose; or remove, alter, or obscure any copyright, trademark, or proprietary notices.
  </p>
}</>
    <h5 style={styles.heading}>6. User Content and License to workbrook</h5>
    <>{
  <p>
    You retain ownership of content, information, resumes, profiles, job descriptions, images, messages, application materials, and other materials that you upload, post, submit, transmit, or otherwise make available through the Services (&quot;User Content&quot;), subject to the rights you grant in these Terms.
By submitting User Content through the Services, you grant workbrook a non-exclusive, worldwide, royalty-free license to host, store, reproduce, process, format, display, distribute, transmit, analyze, adapt, and use that User Content only as reasonably necessary to operate, provide, secure, maintain, improve, and support the Services; facilitate hiring, referrals, recruiting, communication, and related platform functions; generate analytics in aggregated or de-identified form; enforce these Terms; and comply with legal obligations.
You represent and warrant that you own or control all rights necessary to submit the User Content, that the User Content does not violate law or infringe the rights of any third party, and that you have any required consents, permissions, or lawful basis to share personal information contained in the User Content.
  </p>
}</>
    <h5 style={styles.heading}>7. Copyright Infringement; DMCA Policy</h5>
    <>{
  <p>
    workbrook respects the intellectual property rights of others and expects users of the Services to do the same. In accordance with the Digital Millennium Copyright Act (&quot;DMCA&quot;), workbrook will respond to valid notices of alleged copyright infringement and may remove or disable access to content claimed to be infringing.
Designated Agent
If you believe that content on the Services infringes your copyright, you may submit a notice to workbrook&apos;s designated agent:
DMCA Agent
workbrook
Email: dmca@workbrook.com
Address: 26403, Cloverbank LN, Richmond, Texas 77406.
Requirements for a Valid DMCA Notice
Your notice must include:
  </p>
}</>
    <>{
  <ul>
<li>identification of the copyrighted work claimed to have been infringed;</li>
<li>identification of the material that is claimed to be infringing and its location on the Services;</li>
<li>your contact information (name, address, telephone number, and email);</li>
<li>a statement that you have a good faith belief that use of the material is not authorized;</li>
<li>a statement that the information in the notice is accurate and, under penalty of perjury, that you are authorized to act on behalf of the copyright owner;</li>
<li>your physical or electronic signature.</li>
  </ul>
}</>
    <>{
  <p>
    Counter-Notification
If you believe your content was removed in error, you may submit a counter-notification including:
  </p>
}</>
    <>{
  <ul>
<li>identification of the material removed and its prior location;</li>
<li>your contact information;</li>
<li>a statement under penalty of perjury that you have a good faith belief the material was removed by mistake or misidentification;</li>
<li>a statement consenting to the jurisdiction of the courts located in Houston, Texas;</li>
<li>your physical or electronic signature.</li>
  </ul>
}</>
    <>{
  <p>
    Repeat Infringer Policy
workbrook may suspend or terminate accounts of users who are repeat infringers in appropriate circumstances.
Misrepresentation
Any person who knowingly materially misrepresents that material or activity is infringing may be subject to liability.
  </p>
}</>
    <h5 style={styles.heading}>8. Acceptable Use</h5>
    <>{
  <p>
    You agree to use the Services only for lawful, authorized, and intended purposes. You are solely responsible for your use of the Services, your account, your User Content, and your interactions with other users.
You may not upload, post, send, transmit, or otherwise make available content or information that is unlawful, fraudulent, deceptive, misleading, abusive, threatening, defamatory, discriminatory, obscene, sexually explicit, infringing, or otherwise harmful. You may not use the Services in a way that violates privacy rights, confidentiality obligations, anti-spam laws, employment laws, anti-discrimination laws, data protection laws, intellectual property rights, or any other applicable law.
  </p>
}</>
    <h5 style={styles.heading}>9. Prohibited Activities</h5>
    <>{
  <p>
    You agree not to create false accounts, duplicate accounts, or accounts under false pretenses; impersonate any person or entity, or misrepresent your identity, authority, qualifications, work history, or affiliation; post fake jobs, fake candidates, fake applications, fake referrals, or misleading hiring activity; or collude with another user, Employer, Applicant, Recruiter, or Candidate to manipulate payouts, rankings, Match Scores, billing events, or platform outcomes.
You further agree not to use bots, scripts, crawlers, spiders, scrapers, data-mining tools, or other automated means to access or interact with the Services without workbrook&apos;s written authorization; attempt to bypass security, authentication, access controls, or encryption; upload viruses, malware, or other harmful code; interfere with or disrupt the Services; access non-public areas of the Services; or use platform data or output to train artificial intelligence or machine learning systems without authorization.
  </p>
}</>
    <h5 style={styles.heading}>10. Applicant Terms</h5>
    <>{
  <p>
    Applicants may apply to jobs through the Services using their workbrook accounts, subject to job eligibility requirements and these Terms.
By applying to a job through workbrook, you authorize workbrook to transmit your application materials, profile information, resume, and related information to the relevant Employer, Job Poster, Recruiter, or other authorized hiring participant involved in the opportunity; represent and warrant that the information you provide is truthful, current, and not misleading; and acknowledge that Employers, not workbrook, make hiring decisions.
Applicants may withdraw from a job application through available platform tools or by otherwise notifying the relevant party, subject to ongoing legal, compliance, or record-retention requirements.
  </p>
}</>
    <h5 style={styles.heading}>11. No Employment Relationship; No Agency</h5>
    <>{
  <p>
    workbrook provides a technology platform that facilitates connections between candidates, employers, recruiters, and other users. workbrook is not an employer, recruiter of record, staffing agency, or employment agency with respect to users of the Services unless expressly agreed in a separate written agreement.
Nothing in these Terms or the Services creates or is intended to create:
  </p>
}</>
    <>{
  <ul>
<li>an employment relationship;</li>
<li>a joint employment or co-employment relationship;</li>
<li>an agency, partnership, or joint venture;</li>
<li>or any fiduciary relationship between workbrook and any user, including candidates, employers, or referrers.</li>
  </ul>
}</>
    <>{
  <p>
    Employers are solely responsible for:
  </p>
}</>
    <>{
  <ul>
<li>job postings and job descriptions;</li>
<li>candidate evaluation, screening, and selection;</li>
<li>interview processes;</li>
<li>hiring decisions;</li>
<li>compensation, benefits, and employment terms;</li>
<li>compliance with applicable employment, labor, and anti-discrimination laws.</li>
  </ul>
}</>
    <>{
  <p>
    Candidates are solely responsible for:
  </p>
}</>
    <>{
  <ul>
<li>the accuracy of their resumes and applications;</li>
<li>their decision to apply for or accept any role.</li>
  </ul>
}</>
    <>{
  <p>
    workbrook does not:
  </p>
}</>
    <>{
  <ul>
<li>employ candidates on behalf of employers;</li>
<li>control or direct employer hiring decisions;</li>
<li>guarantee interviews, offers, or employment outcomes;</li>
<li>act as an agent for either candidates or employers in any hiring decision.</li>
  </ul>
}</>
    <>{
  <p>
    Any interaction, negotiation, or agreement between a candidate and an employer is solely between those parties. workbrook is not a party to any employment or engagement relationship and bears no responsibility or liability arising from such relationships.
  </p>
}</>
    <h5 style={styles.heading}>12. Independent Status of Referrers</h5>
    <>{
  <p>
    Referrers who use the Services are independent users of the workbrook platform. Referrers are not employees, agents, representatives, contractors, recruiters, partners, joint venturers, franchisees, or legal representatives of workbrook solely by virtue of using the Services.
Unless expressly authorized in writing by workbrook, Referrers have no authority to:
  </p>
}</>
    <>{
  <ul>
<li>bind workbrook to any agreement;</li>
<li>make representations or warranties on behalf of workbrook;</li>
<li>negotiate employment terms on behalf of workbrook;</li>
<li>promise interviews, employment, compensation, or hiring outcomes;</li>
<li>collect payments or fees on behalf of workbrook;</li>
<li>represent themselves as acting on behalf of workbrook.</li>
  </ul>
}</>
    <>{
  <p>
    Any statements, communications, recommendations, or representations made by Referrers are solely their own and do not represent the views, commitments, or obligations of workbrook.
workbrook is not responsible for the acts, omissions, communications, or conduct of Referrers except to the extent expressly required by applicable law.
  </p>
}</>
    <h5 style={styles.heading}>13. Recruiter Terms</h5>
    <>{
  <p>
    To act as a Recruiter on workbrook, you must create a recruiter profile and satisfy workbrook&apos;s eligibility requirements, including any experience, screening, approval, or profile-completion requirements communicated by workbrook.
Recruiters must use the Services only for legitimate and lawful recruiting purposes and remain subject to these Terms. Where workbrook provides a separate Recruiter Agreement or service-specific contract, that agreement supplements these Terms and controls in the event of conflict as to Recruiter services.
  </p>
}</>
    <h5 style={styles.heading}>14. Privacy, Communications, and Data Protection</h5>
    <>{
  <p>
    Your use of the Services is subject to workbrook&apos;s Privacy Policy, which describes how workbrook collects, uses, stores, discloses, and otherwise processes personal data.
You agree not to upload, share, transmit, or otherwise disclose personal information through the Services unless you have the legal right or lawful basis to do so. Employers, Recruiters, and Referrers are independently responsible for their own lawful handling of personal information they receive through the Services.
workbrook may send service-related, transactional, operational, security, billing, legal, and support communications in connection with the Services. Marketing communications are subject to applicable law and any preferences or consents required by law. By using the Services, you consent to receive communications electronically, including agreements, notices, disclosures, and updates.
  </p>
}</>
    <h5 style={styles.heading}>15. User Information; Accuracy and Verification</h5>
    <>{
  <p>
    Users are solely responsible for the accuracy, completeness, and legality of any information, content, or materials they submit, upload, publish, or otherwise make available through the Services, including resumes, profiles, job postings, job descriptions, qualifications, compensation details, and communications.
workbrook does not independently verify, audit, or guarantee:
  </p>
}</>
    <>{
  <ul>
<li>the identity of users;</li>
<li>the accuracy or completeness of candidate information;</li>
<li>the accuracy or completeness of employer job postings;</li>
<li>the accuracy or completeness of recruiter information;</li>
<li>the legitimacy, availability, or terms of any job opportunity;</li>
<li>the qualifications, credentials, or background of any candidate.</li>
  </ul>
}</>
    <>{
  <p>
    Employers are solely responsible for:
  </p>
}</>
    <>{
  <ul>
<li>the content and accuracy of job postings;</li>
<li>hiring criteria and decisions;</li>
<li>compliance with applicable employment, labor, and anti-discrimination laws.</li>
  </ul>
}</>
    <>{
  <p>
    Candidates are solely responsible for:
  </p>
}</>
    <>{
  <ul>
<li>the accuracy of their resumes, profiles, and application materials;</li>
<li>ensuring that all submitted information is truthful and not misleading.</li>
  </ul>
}</>
    <>{
  <p>
    Recruiters are solely responsible for:
  </p>
}</>
    <>{
  <ul>
<li>the accuracy of their information, experience and expertise;</li>
<li>ensuring that all submitted information is truthful and not misleading.</li>
  </ul>
}</>
    <>{
  <p>
    workbrook is not responsible for any misrepresentation, omission, or inaccuracy provided by any user, and disclaims all liability arising from reliance on such information.
Users acknowledge that any reliance on information obtained through the Services is at their own risk.
  </p>
}</>
    <h5 style={styles.heading}>16. No Guarantees of Candidates, Hiring, or Outcomes</h5>
    <>{
  <p>
    workbrook provides a platform to facilitate connections, matching, and recruitment-related workflows between users. workbrook does not guarantee:
  </p>
}</>
    <>{
  <ul>
<li>the quality, suitability, qualifications, or performance of any candidate or applicant;</li>
<li>the accuracy or completeness of candidate or employer information;</li>
<li>that any candidate will be available, interested, or suitable for a role;</li>
<li>that any job posting will receive applications;</li>
<li>that any application will result in an interview, offer, or hire;</li>
<li>that any user will obtain employment, engagements, or opportunities through the Services.</li>
  </ul>
}</>
    <>{
  <p>
    All hiring decisions are made solely by employers, and all application decisions are made solely by candidates. Any interaction, evaluation, or agreement between users is at their own discretion and risk.
workbrook disclaims all liability arising from:
  </p>
}</>
    <>{
  <ul>
<li>the performance, conduct, or actions of any candidate, employer, or user;</li>
<li>any hiring, employment, or engagement outcomes;</li>
<li>any reliance on information, matches, or recommendations provided through the Services.</li>
  </ul>
}</>
    <h5 style={styles.heading}>17. User Disputes; Release</h5>
    <>{
  <p>
    workbrook is not a party to any agreements, arrangements, or relationships formed between users of the Services, including between candidates, employers, referrers, or other users.
Users are solely responsible for their interactions with one another, including any hiring decisions, employment relationships, payments, referrals, or other engagements.
To the fullest extent permitted by law, you agree that workbrook shall not be responsible or liable for any dispute, claim, or controversy arising between users, including any claims relating to:
  </p>
}</>
    <>{
  <ul>
<li>candidate qualifications, performance, or conduct;</li>
<li>job postings, employment terms, or hiring decisions;</li>
<li>payments, compensation, or referral rewards;</li>
<li>communications or representations made by users.</li>
  </ul>
}</>
    <>{
  <p>
    You agree to release and hold harmless workbrook and its affiliates, officers, employees, and agents from any and all claims, demands, damages, losses, and liabilities arising out of or in connection with any dispute between users.
If you are a resident of California, you waive any rights under California Civil Code Section 1542, which provides:
&quot;A general release does not extend to claims which the creditor does not know or suspect to exist in his or her favor at the time of executing the release...&quot;
  </p>
}</>
    <h5 style={styles.heading}>18. AI and Automated Features</h5>
    <>{
  <p>
    workbrook may use artificial intelligence, machine learning, automation, algorithmic ranking, predictive systems, or similar technologies in connection with the Services, including to summarize job descriptions or candidate profiles, calculate Match Scores or relevance signals, rank or prioritize candidates or jobs, detect suspicious activity or fraud, recommend opportunities or candidates, and support outreach or workflow automation.
These outputs (&quot;AI Outputs&quot;) are assistive tools only. AI Outputs may be incomplete, inaccurate, probabilistic, or based on limited or outdated inputs. AI Outputs may also reflect biases, unintended correlations, or discriminatory patterns present in underlying data or model design.
workbrook does not represent or warrant that AI Outputs are:
  </p>
}</>
    <>{
  <ul>
<li>accurate, complete, or reliable;</li>
<li>free from bias, discrimination, or error; or</li>
<li>compliant with applicable employment, labor, or anti-discrimination laws.</li>
  </ul>
}</>
    <>{
  <p>
    AI Outputs do not constitute legal, HR, employment, or professional advice and do not replace human judgment. Users must independently review, validate, and assess all AI Outputs before relying on them.
Users agree that:
  </p>
}</>
    <>{
  <ul>
<li>AI Outputs are provided for informational purposes only;</li>
<li>AI Outputs must not be used as the sole basis for hiring, screening, or employment decisions;</li>
<li>all decisions made using the Services remain the sole responsibility of the user.</li>
  </ul>
}</>
    <>{
  <p>
    Employers are solely responsible for ensuring that their use of the Services, including any use of AI Outputs, complies with all applicable laws, including anti-discrimination and equal employment laws.
workbrook disclaims all liability arising from:
  </p>
}</>
    <>{
  <ul>
<li>reliance on AI Outputs;</li>
<li>any errors, omissions, or inaccuracies in AI Outputs; and</li>
<li>any discriminatory, biased, or unlawful outcomes resulting from the use of AI Outputs.</li>
  </ul>
}</>
    <h5 style={styles.heading}>19. No Banking; No Fiduciary Relationship; Payments Disclaimer</h5>
    <>{
  <p>
    workbrook is a technology platform and not a bank, financial institution, money services business, or payment processor. workbrook does not provide banking services and does not accept deposits.
To the extent the Services include wallets, credits, balances, referral rewards, payouts, or other payment-related features, such amounts:
  </p>
}</>
    <>{
  <ul>
<li>are not bank accounts;</li>
<li>are not deposits;</li>
<li>are not insured by the Federal Deposit Insurance Corporation (FDIC) or any other governmental authority;</li>
<li>do not earn interest; and</li>
<li>may be subject to limits, delays, reversals, or conditions as described in the Services or applicable terms.</li>
  </ul>
}</>
    <>{
  <p>
    workbrook is not a trustee, escrow agent, fiduciary, or custodian with respect to any funds, credits, or balances. Nothing in these Terms creates any fiduciary duty between workbrook and any user, including employers, candidates, or referrers.
All payments, rewards, fees, and transactions facilitated through the Services are provided on a platform basis only. workbrook may rely on third-party payment processors and does not control or guarantee their performance, availability, or security.
Users are solely responsible for:
  </p>
}</>
    <>{
  <ul>
<li>ensuring the accuracy of payment details;</li>
<li>compliance with applicable tax, financial, and regulatory obligations;</li>
<li>understanding the terms applicable to any payments, rewards, or credits.</li>
  </ul>
}</>
    <>{
  <p>
    workbrook is not responsible for:
  </p>
}</>
    <>{
  <ul>
<li>payment delays or failures caused by third-party providers;</li>
<li>loss of funds due to incorrect information provided by users;</li>
<li>disputes between users relating to payments, compensation, or rewards.</li>
  </ul>
}</>
    <>{
  <p>
    No funds held, processed, or referenced through the Services shall be deemed to be held &quot;in trust&quot; unless expressly stated in a separate written agreement signed by workbrook.
  </p>
}</>
    <h5 style={styles.heading}>20. Failed Payments; Charge Authorization; Chargebacks</h5>
    <>{
  <p>
    You agree to provide valid, current, and complete payment information and to maintain such information as necessary for successful billing.
If any payment is declined, reversed, rejected, or otherwise fails for any reason, including insufficient funds, expired payment methods, or payment processor errors (&quot;Failed Payment&quot;):
  </p>
}</>
    <>{
  <ul>
<li>workbrook may retry the charge using the same payment method or any other payment method on file;</li>
<li>workbrook may suspend, restrict, or terminate access to paid features, subscriptions, Credits, or Services until payment is successfully completed;</li>
<li>any associated features, entitlements, Credits, or benefits may be withheld, revoked, or reversed.</li>
  </ul>
}</>
    <>{
  <p>
    You authorize workbrook to re-attempt charges for outstanding balances and any applicable fees.
workbrook is not responsible for Failed Payments caused by:
  </p>
}</>
    <>{
  <ul>
<li>payment processor errors;</li>
<li>card network issues;</li>
<li>financial institution actions;</li>
<li>incorrect or outdated payment information;</li>
<li>user-side restrictions or limits.</li>
  </ul>
}</>
    <>{
  <p>
    If you initiate a chargeback or payment dispute with your financial institution:
  </p>
}</>
    <>{
  <ul>
<li>workbrook may suspend or terminate your account;</li>
<li>workbrook may invalidate associated Credits, rewards, or entitlements;</li>
<li>you remain responsible for all amounts owed unless otherwise determined by workbrook or required by law.</li>
  </ul>
}</>
    <>{
  <p>
    Where payouts, rewards, or benefits depend on successful receipt of payment from another user (including employers or partners), such payouts may be delayed, reduced, or cancelled in the event of Failed Payments.
Users are solely responsible for any taxes, duties, or reporting obligations arising from their use of the Services, including any payments, rewards, or compensation received.
  </p>
}</>
    <h5 style={styles.heading}>21. Service Management and Enforcement</h5>
    <>{
  <p>
    workbrook reserves the right, but not the obligation, to monitor the Services for compliance with these Terms; investigate suspected violations, fraud, abuse, billing disputes, and suspicious activity; remove, disable, restrict, or refuse access to content, jobs, features, accounts, Wallets, or Services; and preserve, review, and disclose information as reasonably necessary to enforce these Terms, comply with law, or protect rights, property, and safety.
workbrook&apos;s records, logs, timestamps, and platform data may be used in determining attribution, billing, fraud, reward eligibility, and compliance outcomes.
  </p>
}</>
    <h5 style={styles.heading}>22. Suspension and Termination</h5>
    <>{
  <p>
    workbrook may suspend, limit, or terminate your access to all or part of the Services immediately where reasonably necessary due to suspected fraud, abuse, manipulation, security risk, unlawful conduct, nonpayment, violation of these Terms, false or misleading account or transaction information, or legal or reputational risk to workbrook or the Services.
Where reasonably practicable, workbrook may provide notice of suspension or termination, but workbrook is not required to do so in advance where urgent action is needed.
Account deletion or discontinuation does not relieve you of obligations accrued before termination, including payment obligations, tax obligations, indemnity obligations, confidentiality obligations, or disputes already in progress.
  </p>
}</>
    <h5 style={styles.heading}>23. Disclaimers</h5>
    <>{
  <p>
    THE SERVICES ARE PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WORKBROOK DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE.
workbrook does not warrant that the Services will be uninterrupted, error-free, secure, or always available; that any job posting, referral, applicant profile, resume, recruiter profile, company information, salary information, Match Score, or platform content is accurate, complete, reliable, or lawful; or that any hire, interview, application, reward, or commercial outcome will occur.
Users are solely responsible for conducting their own due diligence, screening, interviews, reference checks, background checks, legal compliance, and business judgment.
  </p>
}</>
    <h5 style={styles.heading}>24. Limitation of Liability</h5>
    <>{
  <p>
    To the maximum extent permitted by law, workbrook and its affiliates, officers, directors, employees, contractors, licensors, and agents will not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, including loss of profits, loss of revenue, loss of opportunity, loss of data, business interruption, reputational harm, or procurement of substitute services, arising out of or relating to the Services or these Terms, even if advised of the possibility of such damages.
To the maximum extent permitted by law, workbrook&apos;s aggregate liability for all claims arising out of or relating to the Services or these Terms will not exceed the greater of the amount you paid to workbrook in the 12 months preceding the event giving rise to the claim or USD 100. Nothing in these Terms excludes liability that cannot lawfully be excluded or limited.
  </p>
}</>
    <h5 style={styles.heading}>25. Indemnification</h5>
    <>{
  <p>
    You agree to defend, indemnify, and hold harmless workbrook, its affiliates, and their respective officers, directors, employees, contractors, licensors, and agents from and against any claims, liabilities, damages, judgments, losses, costs, and expenses, including reasonable attorneys&apos; fees, arising out of or related to your use of the Services, your violation of these Terms, your User Content, your violation of any law or the rights of a third party, your misuse of personal information or candidate data, or any fraud, misrepresentation, spam, discriminatory hiring activity, fake postings, fake referrals, or other prohibited conduct by you.
  </p>
}</>
    <h5 style={styles.heading}>26. Modifications, Interruptions, and Service Changes</h5>
    <>{
  <p>
    workbrook may modify, suspend, discontinue, or update any aspect of the Services at any time, including features, pricing, tools, algorithms, integrations, tiers, reward structures, or availability. workbrook is not liable for any unavailability, downtime, interruption, maintenance event, feature withdrawal, pricing change, or service change, except as required by law or expressly agreed in writing.
workbrook may make changes to these Terms from time to time. If we make material changes, we may provide notice through the Services, by email, or by other reasonable means. Your continued use of the Services after the effective date of updated Terms constitutes acceptance of the updated Terms.
workbrook shall not be liable for any failure or delay resulting from causes beyond its reasonable control, including outages, infrastructure failures, or third-party service disruptions.
Beta Features. workbrook may designate certain features, tools, workflows, integrations, AI functions, dashboards, or services as beta, pilot, early-access, preview, or test features (&quot;Beta Features&quot;). Beta Features may be incomplete, unstable, unavailable, or subject to errors, interruptions, data loss, or material change. workbrook may modify, suspend, or discontinue Beta Features at any time, with or without notice, and has no obligation to support, maintain, correct, or continue Beta Features. Beta Features are provided on an &quot;as is&quot; and &quot;as available&quot; basis, and users use them at their own risk.
  </p>
}</>
    <h5 style={styles.heading}>27. Dispute Resolution and Governing Law</h5>
    <>{
  <p>
    These Terms are governed by the laws of the State of Texas, without regard to conflict of laws principles.
Before filing a formal claim, each party agrees to first attempt to resolve the dispute informally by providing written notice to the other party describing the nature of the dispute and the requested relief. The parties agree to use good faith efforts to resolve the dispute within thirty (30) days of such notice.
If the dispute is not resolved informally, either party may elect to submit the dispute to non-binding mediation in Texas.
If the dispute is not resolved through informal resolution or mediation, then, except as otherwise provided below, the dispute shall be resolved by final and binding arbitration, rather than in court.
Arbitration shall be administered by the American Arbitration Association in accordance with its applicable rules. The arbitration shall take place in Houston, Texas, unless otherwise required by applicable law.
You and workbrook agree that:
  </p>
}</>
    <>{
  <ul>
<li>any arbitration will be conducted on an individual basis only, and not as a class, consolidated, or representative action;</li>
<li>you waive any right to participate in a class action or class-wide arbitration;</li>
<li>the arbitrator shall have exclusive authority to resolve any dispute relating to the interpretation, applicability, or enforceability of this arbitration provision.</li>
  </ul>
}</>
    <>{
  <p>
    Notwithstanding the foregoing:
  </p>
}</>
    <>{
  <ul>
<li>either party may bring an individual claim in small claims court if the claim qualifies;</li>
<li>either party may seek injunctive or equitable relief in a court of competent jurisdiction for matters relating to misuse of the Services, fraud, or intellectual property.</li>
  </ul>
}</>
    <>{
  <p>
    If, for any reason, a claim proceeds in court rather than arbitration, you and workbrook each waive any right to a jury trial.
This dispute resolution provision shall survive termination of these Terms.
  </p>
}</>
    <h5 style={styles.heading}>28. Miscellaneous</h5>
    <>{
  <p>
    These Terms, together with any policies, pricing pages, order forms, service-specific agreements, and other terms expressly incorporated by reference, constitute the entire agreement between you and workbrook regarding the Services, except where a separate written agreement governs a specific service.
If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force and effect. workbrook&apos;s failure to enforce any provision is not a waiver of its right to do so later. You may not assign or transfer these Terms without workbrook&apos;s prior written consent. workbrook may assign these Terms in connection with a merger, acquisition, reorganization, sale of assets, or by operation of law.
For legal notices or questions regarding these Terms, contact: info@workbrook.com.
  </p>
}</>
  </div>
);

const EmployerSection = () => (
  <div>
    <p style={styles.sectionLabel}>Employer / Marketplace</p>
    <h2 style={styles.sectionTitle}>Employer / Marketplace</h2>
    <h5 style={styles.heading}>1. Scope and Relationship to Other Documents</h5>
    <>{
  <p>
    These Employer / Marketplace Commercial Terms (&quot;Employer Commercial Terms&quot;) govern an Employer&apos;s use of workbrook&apos;s paid employer-facing marketplace and hiring services, including tiered job promotion, Wallet funding, usage-based billing, placement services, guarantees, and related commercial mechanics.
These Employer Commercial Terms are incorporated into and form part of the workbrook Terms of Use. In the event of conflict, these Employer Commercial Terms control for the commercial subjects they expressly govern. Separate signed proposals, order forms, search agreements, or client agreements control to the extent they expressly override these Employer Commercial Terms.
  </p>
}</>
    <h5 style={styles.heading}>2. Employer Eligibility and Authority</h5>
    <>{
  <p>
    By posting a job or purchasing any paid employer service, you represent and warrant that the job is real, lawful, and currently open or reasonably anticipated to open; that you are authorized to recruit for the role; that the job description, company information, salary information, and hiring details are accurate in all material respects; and that your use of workbrook complies with applicable employment, anti-discrimination, and privacy laws.
You may not post speculative, misleading, already-filled, unlawful, discriminatory, or confidential jobs.
  </p>
}</>
    <h5 style={styles.heading}>3. Employer Conduct and Reporting Obligations</h5>
    <>{
  <p>
    Employers must keep job postings current and accurate; promptly mark roles as filled, paused, canceled, or closed; provide materially accurate information about job requirements, compensation, location, reporting structure, and work authorization requirements; use Candidate information only for legitimate hiring-related purposes; and comply with all applicable laws in reviewing, contacting, interviewing, and hiring Candidates.
Employers must also provide reasonably timely and truthful updates regarding interviews, offers, acceptances, Start Dates, compensation, and terminations where required for billing, referral rewards, audit, or dispute resolution.
workbrook does not act as an employer nor agent of the Employer.
  </p>
}</>
    <h5 style={styles.heading}>4. Candidate Attribution and Ownership</h5>
    <>{
  <p>
    A Candidate is considered introduced by workbrook when workbrook, a Referrer through workbrook, a Recruiter through workbrook, or any workbrook-supported process first provides or tracks the Candidate in connection with a role.
Unless otherwise agreed in writing, workbrook&apos;s attribution period for introduced or referred Candidates is 12 months from the most recent material workbrook-tracked activity involving that Candidate and the Employer. If an Employer hires, engages, or causes a Candidate to be hired or engaged directly or indirectly during the attribution period, workbrook may assess the applicable fee or treat the hire as reward-eligible under the applicable workbrook terms.
For the purposes of this Section, &apos;material workbrook-tracked activity&apos; is strictly defined as one or more of the following verifiable platform events: (a) a formal job application submitted by the Candidate, (b) a documented interview scheduled or facilitated via the Services, or (c) direct, mutual written correspondence between the Employer and the Candidate initiated through the platform. This clause is associated with Tier 3 and/or Recruiter placement services.
workbrook does not verify the accuracy of candidate inputs used for match scores or AI outputs. workbrook does not guarantee candidate quality, performance, or hiring outcomes.
  </p>
}</>
    <h5 style={styles.heading}>5. Tier 1 - Direct-to-Talent</h5>
    <>{
  <p>
    Tier 1 provides standard visibility in the workbrook job feed and may include basic posting functionality. Unless workbrook states otherwise, Tier 1 does not include premium ranking, white-glove search, AI-assisted outreach, or pay-per-view referral amplification.
workbrook may impose fair-use limits on free job postings, active jobs, editing frequency, refreshes, visibility boosts, or other free-tier features.
  </p>
}</>
    <h5 style={styles.heading}>6. Tier 2 - Community Boosted</h5>
    <>{
  <p>
    Tier 2 is a usage-based service in which an Employer funds a Wallet and is charged a stated View Rate or other defined usage rate for qualifying access events generated through referral or premium discovery mechanisms.
A billable View is determined by workbrook&apos;s tracking systems and may exclude duplicate views by the same user within a defined period, bot traffic, fraudulent or invalid activity, accidental or incomplete loads, and any event workbrook determines should not be charged in its discretion.
workbrook may allow Employers to set daily or total spend caps. Once a cap or available Wallet balance is exhausted, workbrook may pause, reduce, or revert the job&apos;s premium exposure until funding is restored or settings are changed. Except where required by law or expressly stated by workbrook, valid usage charges are non-refundable once incurred.
  </p>
}</>
    <h5 style={styles.heading}>7. Tier 3 - Full Placement / Performance-Based Search</h5>
    <>{
  <p>
    Tier 3 is a premium, performance-based hiring service that may include targeted sourcing, referral bounty activation, AI-assisted talent discovery or passive outreach, priority screening or ranking, and white-glove support.
Unless otherwise agreed in writing, activation of Tier 3 may require a non-refundable retainer. The remaining fee structure, placement percentages, role bands, and invoicing triggers are as stated in the applicable order form, proposal, pricing page, or service-specific agreement.
If Tier 3 is governed by a separate client agreement or search agreement, that agreement controls to the extent of any conflict.
  </p>
}</>
    <h5 style={styles.heading}>8. Wallets and Credits</h5>
    <>{
  <p>
    workbrook may offer Wallet functionality for prepaid balances, purchased credits, referral rewards, promotional credits, or similar value units. Unless workbrook states otherwise, purchased Wallet balances are non-interest-bearing, promotional credits have no cash value and may expire or be withdrawn, Wallet balances are non-transferable except as expressly permitted by workbrook, and workbrook may distinguish between purchased credits and promotional credits for redemption order, refund treatment, or expiration.
Employers are responsible for maintaining sufficient Wallet balance where a service requires prepaid funding.
Any amounts added to a Wallet represent a prepayment for services and are not held as funds, deposits, or stored monetary value. Upon funding, such amounts may be converted into Credits or purchasing capacity for use within the Services.
  </p>
}</>
    <h5 style={styles.heading}>9. Billing, Invoicing, and Payment</h5>
    <>{
  <p>
    You authorize workbrook and its payment processors to charge your selected payment method for fees you incur. You must provide accurate billing, tax, and payment information and keep that information current. You are responsible for any fees charged by your payment provider, bank, card issuer, or payment processor.
Except where required by law or expressly stated by workbrook in writing, fees already incurred for valid Services, usage events, retainers, consumed credits, subscriptions already billed, and completed service milestones are non-refundable.
If a payment fails, is reversed, is charged back, or is disputed, workbrook may suspend your access to paid Services, recover or offset amounts owed, invalidate related rewards or promotional benefits where appropriate, and pursue collection costs and legal remedies.
workbrook does not hold funds in escrow and does not act as a financial intermediary or fiduciary.
  </p>
}</>
    <h5 style={styles.heading}>10. Taxes</h5>
    <>{
  <p>
    Employers are responsible for all taxes, duties, levies, withholdings, and other governmental charges arising from their use of paid Services, except taxes imposed on workbrook&apos;s net income. workbrook may withhold amounts where required by law.
  </p>
}</>
    <h5 style={styles.heading}>11. Anti-Circumvention</h5>
    <>{
  <p>
    Employers may not bypass workbrook to avoid applicable fees, billing rules, or referral reward obligations. Circumvention includes hiring or engaging a workbrook-introduced or workbrook-referred Candidate off-platform to avoid fees; requesting that Applicants, Candidates, Referrers, or Recruiters continue outside workbrook for fee-avoidance purposes; and failing to report a hire or material hiring event connected to a workbrook-generated Candidate.
Where workbrook reasonably determines circumvention has occurred, workbrook may charge the applicable placement fee or marketplace fee, deny or recover promotional benefits, suspend or terminate the Employer account, withhold services, and pursue additional remedies available under law or contract.
  </p>
}</>
    <h5 style={styles.heading}>12. Audits, Disputes, and workbrook Records</h5>
    <>{
  <p>
    workbrook may use its records, logs, timestamps, analytics, billing data, traffic filters, and platform data to determine usage-based billing, attribution, circumvention, reward eligibility, and compliance outcomes.
Employers agree to cooperate in good faith with reasonable requests for confirmation of interviews, offers, salary, Start Date, and termination data relevant to billing, rewards, or guarantees.
  </p>
}</>
    <h5 style={styles.heading}>13. Governing Terms</h5>
    <>{
  <p>
    All disclaimers, liability limitations, indemnities, dispute-resolution terms, and miscellaneous provisions in the workbrook Terms of Use apply to these Employer Commercial Terms unless a separate signed agreement expressly states otherwise.
  </p>
}</>
  </div>
);

const ReferralSection = () => (
  <div>
    <p style={styles.sectionLabel}>Referral Rewards</p>
    <h2 style={styles.sectionTitle}>Referral Rewards</h2>
    <h5 style={styles.heading}>1. Scope and Relationship to Other Documents</h5>
    <>{
  <p>
    These Referral Rewards Terms (&quot;Referral Terms&quot;) govern Referral validity, attribution, reward eligibility, payout timing, fraud controls, taxes, and disputes for any Referral Reward or bounty program made available through workbrook.
These Referral Terms are incorporated into and form part of the workbrook Terms of Use. In the event of conflict, these Referral Terms control for the reward subjects they expressly govern. Job-specific reward notices, campaign terms, or employer-specific reward programs may supplement these Referral Terms and will control to the extent they expressly vary the general program rules.
  </p>
}</>
    <h5 style={styles.heading}>2. Referrer Eligibility</h5>
    <>{
  <p>
    Any eligible user may make a Referral through the Services, subject to these Terms and any role, geography, campaign, or job-specific restrictions stated by workbrook.
By making a Referral, you represent and warrant that you are acting lawfully and in good faith; that you have a reasonable basis to believe the referred person may be relevant to the role; that you have any required permission or lawful basis to share the referred person&apos;s information or to contact them in connection with the opportunity; and that you will not engage in spam, mass low-quality submissions, fake referrals, self-referrals, or manipulation.
  </p>
}</>
    <h5 style={styles.heading}>3. When a Referral Is Credited</h5>
    <>{
  <p>
    A Referral is credited when workbrook&apos;s systems determine that a Candidate was referred through your unique workbrook-tracked referral mechanism and the referral satisfies workbrook&apos;s attribution rules.
A Referral may be deemed invalid if the Candidate was already in the Employer&apos;s active pipeline; the Candidate had already applied or been submitted for the role within the relevant lookback period; the referral cannot be verified; the referral resulted from prohibited conduct; or the referral is duplicated and another Referrer has valid prior timestamp priority.
Unless workbrook states otherwise for a campaign or service, duplicate referrals are resolved on a first valid submission basis using workbrook&apos;s records. In the event of duplicate referrals for the exact same Candidate and role, the Referral Reward will be exclusively credited to the Referrer whose unique tracking link generated the earliest timestamped application or initial account creation, as recorded down to the millisecond by workbrook&apos;s internal server logs.
Credits are distinct from referral rewards and do not constitute earned compensation.
  </p>
}</>
    <h5 style={styles.heading}>4. Successful Referral and Reward Trigger</h5>
    <>{
  <p>
    A Referral Reward is earned only if the referred Candidate becomes a verified Hire for a Tier 3 job, the Hire is attributable to your Referral under workbrook&apos;s systems and rules, any applicable waiting period, Guarantee Period, fraud checks, employer confirmation steps, and payment conditions are satisfied, and you remain in compliance with the workbrook documents.
Unless otherwise stated by workbrook, a referral is not reward-eligible merely because a Candidate clicks, views, applies, interviews, or receives an offer.
Referral rewards are conditional platform incentives and do not constitute held funds, wages, or guaranteed compensation.
  </p>
}</>
    <h5 style={styles.heading}>5. Reward Amounts</h5>
    <>{
  <p>
    Reward percentages, fixed amounts, role bands, payout schedules, and promotional reward structures may vary by job, campaign, geography, tier, or Employer arrangement. Where workbrook publishes a specific reward formula for a role or campaign, that published formula applies.
Unless explicitly included, bonuses, equity, relocation packages, and non-cash benefits are excluded from salary-based reward calculations. workbrook may round, adjust, or recalculate rewards to reflect verified salary information, corrected data, currency conversion, taxes, caps, minimums, maximums, or employer-specific terms.
  </p>
}</>
    <h5 style={styles.heading}>6. Payout Timing and Conditions</h5>
    <>{
  <p>
    workbrook may pay rewards in stages, including milestone-based structures such as partial payment after 30 days of employment and the balance after completion of the Guarantee Period.
Before payout, workbrook may require identity verification, payment account verification, tax documentation, fraud screening, employer confirmation of salary, Start Date, and role details, and confirmation that the Hire remains in good standing through the relevant milestone date.
workbrook may delay, withhold, reverse, reduce, or deny payout where fraud, abuse, collusion, or manipulation is suspected; the Employer disputes attribution in good faith; the Employer has not paid workbrook where Employer payment is a condition of the reward model; required tax or verification information has not been provided; the Candidate did not satisfy the required employment milestone; or workbrook determines the Referral is invalid under these Terms.
Referral payouts are contingent on successful payment collection and may be withheld in the event of Failed Payments or chargebacks.
  </p>
}</>
    <h5 style={styles.heading}>7. Referrer Conduct Restrictions</h5>
    <>{
  <p>
    Referrers may not refer themselves; offer kickbacks, side-payments, or reward-sharing arrangements to Candidates, hiring managers, recruiters, or Employer representatives for the purpose of manipulating outcomes; submit Candidates without authorization where such authorization is required by law; create multiple accounts to increase reward eligibility; or manipulate referral timestamps, links, identities, or platform behavior.
workbrook may suspend referral privileges, invalidate Referrals, reverse rewards, or terminate accounts for any violation of this Section.
  </p>
}</>
    <h5 style={styles.heading}>8. Taxes and Compliance</h5>
    <>{
  <p>
    Referrers are solely responsible for any taxes, duties, levies, withholdings, or reporting obligations arising from any Referral Reward, except to the extent workbrook is required by law to withhold or report amounts.
workbrook may require completion of tax forms, identity verification, payment verification, or compliance checks before releasing a reward and may withhold or report amounts where required by law.
  </p>
}</>
    <h5 style={styles.heading}>9. Employer Nonpayment and Dependency Risks</h5>
    <>{
  <p>
    Where a reward program is funded or economically dependent on an Employer payment obligation, workbrook may condition reward payout on workbrook&apos;s receipt of the corresponding Employer payment. If an Employer fails to pay, becomes insolvent, disputes attribution in good faith, or otherwise prevents completion of the funding event, workbrook may delay, reduce, or decline payout unless workbrook expressly agrees otherwise in writing.
Referrers acknowledge and agree that workbrook&apos;s obligation to issue a Referral Reward is strictly contingent upon workbrook&apos;s actual receipt of the corresponding placement fee from the Employer. While workbrook will make commercially reasonable efforts to collect owed fees, Employer default, insolvency, or breach of contract may result in the delay or cancellation of the Referral Reward without liability to workbrook
  </p>
}</>
    <h5 style={styles.heading}>10. Disputes and Final Determinations</h5>
    <>{
  <p>
    workbrook may use its records, timestamps, link tracking, platform events, billing data, communications logs, and other platform evidence to determine Referral validity, duplicate priority, reward eligibility, fraud, and payout amount.
In the event of a dispute about a Referral, duplicate claim, attribution, or reward amount, workbrook&apos;s good-faith determination based on its records will control unless a separate written agreement or applicable law requires otherwise.
  </p>
}</>
    <h5 style={styles.heading}>11. Governing Terms</h5>
    <>{
  <p>
    All disclaimers, liability limitations, indemnities, dispute-resolution terms, and miscellaneous provisions in the workbrook Terms of Use apply to these Referral Terms unless a separate written agreement expressly states otherwise.
  </p>
}</>
  </div>
);

const CandidateSection = () => (
  <div>
    <p style={styles.sectionLabel}>Candidate Product Terms</p>
    <h2 style={styles.sectionTitle}>Candidate Product Terms</h2>
    <h5 style={styles.heading}>1. Scope and Relationship to Other Documents</h5>
    <>{
  <p>
    These Candidate Product Terms (&quot;Candidate Terms&quot;) govern candidate-facing match tools, premium match products, URL scan features, peer benchmarking features, AI-generated career assistance tools, credits, packs, subscriptions, and related functionality made available through workbrook.
These Candidate Terms are incorporated into and form part of the workbrook Terms of Use. In the event of conflict, these Candidate Terms control for the candidate-facing product subjects they expressly govern. Job-specific notices, checkout disclosures, feature pages, promotional campaigns, or product-specific purchase terms may supplement these Candidate Terms and will control to the extent they expressly vary the general program rules.
  </p>
}</>
    <h5 style={styles.heading}>2. Covered Candidate Product Features</h5>
    <>{
  <p>
    Candidate Product Features may include, without limitation, On-Platform Job Match, Any Job URL Match, Peer-Match Score, Peer Insights, AI Rewrite Suggestions, scan packs, subscription-based access, feature bundles, trial access, promotional entitlements, and related candidate-support tools made available through the Services.
All Candidate Product Features remain part of the Services and are also subject to the workbrook Privacy Policy, any pricing or checkout terms presented at the time of purchase, and the disclaimers, liability limitations, dispute-resolution terms, and other incorporated provisions of the workbrook legal document set.
  </p>
}</>
    <h5 style={styles.heading}>3. On-Platform Job Match</h5>
    <>{
  <p>
    workbrook may provide On-Platform Job Match for jobs posted, displayed, or otherwise made available within the workbrook platform. On-Platform Job Match may include match percentages, compatibility indicators, skill-fit signals, missing-skill highlights, ranking outputs, recommendations, and other relevance-based summaries.
On-Platform Job Match is provided for informational purposes only. workbrook does not guarantee that any result, score, signal, or recommendation is accurate, complete, reflects the full criteria used by an employer, predicts interview selection, predicts job suitability, or predicts hiring outcome.
workbrook may modify the methodology, weighting, inputs, logic, display, limits, or availability of On-Platform Job Match at any time.
workbrook does not verify job postings or hiring criteria.
  </p>
}</>
    <h5 style={styles.heading}>4. Any Job URL Match</h5>
    <>{
  <p>
    workbrook may permit users to submit, upload, paste, or scan external job postings using third-party URLs.
By using Any Job URL Match, you acknowledge and agree that you are responsible for the URL and any related data you submit; workbrook does not own, control, endorse, or guarantee any third-party website or job posting; workbrook does not guarantee that any external URL can be accessed, parsed, read, imported, or processed successfully; third-party sites may change, remove, restrict, block, or interfere with access to job content at any time; and workbrook may limit, reject, delay, or disable scans for certain domains, sources, job types, geographies, or technical reasons.
workbrook is not responsible for broken links, inaccessible pages, parsing failures, formatting issues, removed postings, outdated content, or changes made by third-party sites. Your use of third-party job sites remains subject to those third parties&apos; own terms and policies.
  </p>
}</>
    <h5 style={styles.heading}>5. Peer-Match Score and Peer Insights</h5>
    <>{
  <p>
    workbrook may provide Peer-Match Score and Peer Insights to help users understand how their profile, resume, qualifications, or experience compares to relevant benchmarks.
Peer-Match Score and Peer Insights may be based on user-submitted information, workbrook profile data, resume data, job-description content, aggregated platform information, inferred categories, model-generated comparison sets, or market-facing data sources selected by workbrook.
By using Peer-Match Score or Peer Insights, you acknowledge and agree that any peer group, percentile, &quot;top peer,&quot; &quot;top 5%,&quot; &quot;industry peer,&quot; or similar benchmark is an estimate only; workbrook does not guarantee that any benchmark is exhaustive, current, or statistically representative; outputs may be inferred, probabilistic, incomplete, subjective, or incorrect; such outputs are not factual statements about your actual market ranking, interview chances, or employability; and workbrook may change the comparison methodology, peer logic, data inputs, thresholds, and output design at any time.
Peer-Match Score and Peer Insights are informational decision-support tools only and are not employment, compensation, recruiting, legal, or career advice.
  </p>
}</>
    <h5 style={styles.heading}>6. Basic and Premium Insight Levels</h5>
    <>{
  <p>
    workbrook may offer Candidate Product Features in different versions, tiers, or packages, including free access, limited access, trial access, one-time purchase packs, weekly subscriptions, monthly subscriptions, premium or deep-insight versions, and promotional or bundled access.
Without limitation, workbrook may distinguish between a basic Peer-Match Score output, such as a simple percentage or limited summary, and a premium or deep-insight output, such as missing skills, competitive gaps, expanded analysis, or suggested improvements.
workbrook may add, remove, rename, bundle, unbundle, limit, suspend, or discontinue any feature level, pricing tier, entitlement, or package at any time, subject to applicable law.
  </p>
}</>
    <h5 style={styles.heading}>7. AI Rewrite Suggestions</h5>
    <>{
  <p>
    workbrook may provide AI Rewrite Suggestions to help users improve resumes, profiles, summaries, bullet points, application materials, or other career documents.
AI Rewrite Suggestions may include rewritten bullet points, skill-based resume edits, phrasing suggestions, summary rewrites, keyword recommendations, role-tailored edits, and competitive-gap response suggestions.
By using AI Rewrite Suggestions, you acknowledge and agree that all AI Rewrite Suggestions are automatically generated and may contain errors, omissions, generic language, unsuitable phrasing, or incorrect assumptions; you are solely responsible for reviewing, editing, validating, and approving any generated content before using it; workbrook does not guarantee that any AI Rewrite Suggestion is accurate, truthful, lawful, non-infringing, employer-compliant, or effective; and you must not use any generated content that is false, misleading, fabricated, defamatory, or otherwise unlawful.
workbrook is not responsible for any outcome arising from your use, non-use, or submission of AI Rewrite Suggestions.
  </p>
}</>
    <h5 style={styles.heading}>8. Credits, Packs, and Subscriptions</h5>
    <>{
  <p>
    Candidate Product Features may be offered through free access, paid Credits, one-time packs, weekly subscriptions, monthly subscriptions, promotional offers, or other purchase models presented through the Services.
Unless workbrook expressly states otherwise, Credits and feature entitlements are non-transferable; promotional Credits have no cash value; unused Credits may expire if an expiration period is disclosed at the time of purchase or grant; scan packs, premium feature unlocks, and similar entitlements are non-refundable once used, activated, consumed, or made available, except where required by law; subscriptions may automatically renew if disclosed at checkout; you authorize recurring charges where applicable based on the purchase terms shown at checkout; and workbrook may impose fair-use limits, anti-abuse thresholds, scan caps, rate limits, and technical restrictions.
workbrook may distinguish between purchased Credits and promotional Credits for purposes of redemption order, expiration, eligibility, or refund treatment.
Credits, scan packs, and similar feature entitlements (&quot;Credits&quot;) are digital access rights that allow use of certain Candidate Product Features. Credits are not currency, stored value, or monetary instruments.
Unless expressly stated otherwise:
  </p>
}</>
    <>{
  <ul>
<li>Credits have no cash value;</li>
<li>Credits are not redeemable, refundable, or exchangeable for money;</li>
<li>Credits do not constitute deposits or property rights;</li>
<li>Credits cannot be withdrawn, transferred, sold, or assigned;</li>
<li>unused Credits are forfeited upon expiration, termination of your account, or discontinuation of the applicable feature.</li>
  </ul>
}</>
    <>{
  <p>
    workbrook may modify, revoke, expire, or discontinue Credits at any time, subject to applicable law.
The loss of unused Credits upon account closure, inactivity, or termination does not entitle you to any compensation.
Access to Candidate Product Features may be suspended in the event of Failed Payments.
  </p>
}</>
    <h5 style={styles.heading}>9. Prepaid Services; No Stored Funds:</h5>
    <>{
  <p>
    Any amounts added to a Wallet represent a prepayment for services and are not held as funds, deposits, or stored monetary value. Upon funding, such amounts may be converted into Credits or purchasing capacity for use within the Services.
  </p>
}</>
    <h5 style={styles.heading}>10. No Guarantee of Interview, Offer, or Hire</h5>
    <>{
  <p>
    Candidate Product Features are intended to support users in their job search, but they do not guarantee any result.
Use of Candidate Product Features does not guarantee job matches, better applications, employer responses, interviews, offers, hires, improved competitiveness, or improved career outcomes. All hiring decisions are made by employers or their representatives, not by workbrook.
Use of match scores, peer insights, or AI tools does not create any employment relationship with workbrook.
  </p>
}</>
    <h5 style={styles.heading}>11. Beta Features and Feature Availability</h5>
    <>{
  <p>
    Some Candidate Product Features may be designated or operated as beta, pilot, preview, early-access, experimental, or limited-release features.
Such features may be incomplete, unstable, unavailable, changed frequently, or withdrawn without notice. workbrook may modify, suspend, restrict, replace, or discontinue any Candidate Product Feature, pricing model, package, score logic, or feature availability at any time, with or without notice, unless required otherwise by law.
Candidate Product Features are provided on an &quot;as is&quot; and &quot;as available&quot; basis, and workbrook has no obligation to maintain, support, correct, or continue any specific Candidate Product Feature.
  </p>
}</>
    <h5 style={styles.heading}>12. Fraud, Abuse, and Misuse</h5>
    <>{
  <p>
    workbrook may suspend, restrict, or terminate access to Candidate Product Features, invalidate Credits, cancel subscriptions, or deny feature access where workbrook reasonably suspects fraud, account sharing, automated misuse, scraping, payment abuse, chargebacks, manipulation of scans, scores, or feature outputs, or use inconsistent with the Terms of Use or these Candidate Terms.
workbrook may also reverse promotional access, deny refunds, or limit future purchases where misuse is detected.
  </p>
}</>
    <h5 style={styles.heading}>13. External Content Disclaimer</h5>
    <>{
  <p>
    Where Candidate Product Features reference, interact with, or process third-party content, including job boards, employer career pages, or external sources, workbrook makes no representation or warranty regarding authorization, ownership, legality, availability, continuity, formatting, completeness, accuracy, or timeliness.
workbrook is not responsible for third-party content or third-party site behavior.
  </p>
}</>
    <h5 style={styles.heading}>14. Candidate Responsibility</h5>
    <>{
  <p>
    You are solely responsible for the accuracy of the information you provide, the final content of your resume, profile, and applications, ensuring that any submitted material is truthful and lawful, and determining whether to rely on any Candidate Product Feature.
You remain responsible for your professional judgment, job-search decisions, and application strategy.
  </p>
}</>
    <h5 style={styles.heading}>15. Governing Terms</h5>
    <>{
  <p>
    All disclaimers, liability limitations, indemnities, dispute-resolution terms, and miscellaneous provisions in the workbrook Terms of Use apply to these Candidate Terms unless a separate written agreement expressly states otherwise.
  </p>
}</>
  </div>
);

const TermsAndConditions = () => {
  const articleRef = useRef(null);
  const topRef = useRef(null);
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [showTop, setShowTop] = useState(false);

  // Walk rendered article to build a grouped TOC and assign stable ids to h5s.
  useEffect(() => {
    if (!articleRef.current) return undefined;
    const root = articleRef.current;
    const nodes = root.querySelectorAll("h2, h5");
    const groups = [];
    let current = null;
    const used = new Set();
    nodes.forEach((el, i) => {
      if (el.tagName === "H2") {
        current = { groupTitle: el.textContent.trim(), items: [] };
        const base = slugify(el.textContent, "group-" + i);
        let id = base;
        let n = 1;
        while (used.has(id)) id = base + "-" + ++n;
        used.add(id);
        el.id = id;
        current.groupId = id;
        groups.push(current);
      } else if (el.tagName === "H5" && current) {
        const base = slugify(el.textContent, "s-" + i);
        let id = base;
        let n = 1;
        while (used.has(id)) id = base + "-" + ++n;
        used.add(id);
        el.id = id;
        current.items.push({ id, heading: el.textContent.trim() });
      }
    });
    setToc(groups);

    const targets = root.querySelectorAll("h5[id]");
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0] && visible[0].target && visible[0].target.id) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    targets.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 360);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    if (topRef.current && topRef.current.scrollIntoView) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <main className="wb-legal" ref={topRef}>
      <style>{LEGAL_CSS}</style>

      {/* Hero */}
      <header className="wb-legal__hero">
        <div className="wb-legal__hero-orb wb-legal__hero-orb--a" aria-hidden="true" />
        <div className="wb-legal__hero-orb wb-legal__hero-orb--b" aria-hidden="true" />
        <div className="wb-legal__hero-inner">
          <span className="wb-legal__eyebrow">
            <span className="wb-legal__eyebrow-dot" />
            Legal
          </span>
          <h1 className="wb-legal__title">Terms &amp; Conditions</h1>
          <p className="wb-legal__lede">
            These Terms explain the rules for using Workbrook — what you can expect
            from us and what we need from you. Please read them carefully.
          </p>
        </div>
      </header>

      {/* Body */}
      <div className="wb-legal__wrap">
        <div className="wb-legal__grid">
          <aside className="wb-legal__toc" aria-label="Table of contents">
            <div className="wb-legal__toc-inner">
              <div className="wb-legal__toc-title">On this page</div>
              {toc.map((g) => (
                <div key={g.groupId} className="wb-legal__toc-group">
                  <a href={"#" + g.groupId} className="wb-legal__toc-group-label">
                    {g.groupTitle}
                  </a>
                  <ul className="wb-legal__toc-list">
                    {g.items.map((s) => (
                      <li key={s.id}>
                        <a
                          href={"#" + s.id}
                          className={
                            "wb-legal__toc-link" +
                            (activeId === s.id ? " wb-legal__toc-link--active" : "")
                          }
                        >
                          <span className="wb-legal__toc-marker" />
                          <span>{s.heading}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </aside>

          <article className="wb-legal__article" ref={articleRef}>
            <TermsOfUseSection />
            <EmployerSection />
            <ReferralSection />
            <CandidateSection />

            <div className="wb-legal__contact">
              <strong>Need to speak with legal?</strong> Email{" "}
              <a href="mailto:info@workbrook.com">info@workbrook.com</a> and
              we&apos;ll get back to you shortly.
            </div>
          </article>
        </div>
      </div>

      {/* Back to top */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Back to top"
        className="wb-legal__top"
        data-show={showTop ? "true" : undefined}
      >
        ↑
      </button>
    </main>
  );
};

export default TermsAndConditions;
